import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { Receivable } from "@/lib/types";
import { money, fmtDate } from "@/lib/format";
import { downloadCsv } from "@/lib/export";
import { PageHeader, EmptyState } from "@/components/shared";
import { toast } from "sonner";
import { Loader2, Download, Clock } from "lucide-react";

const SETTLEMENT_LABELS: Record<string, string> = {
  creada: "Venta creada",
  pagada_pendiente_liquidacion: "Pagada, pend. liquidación",
  liquidada_pendiente_deposito: "Liquidada, pend. depósito",
  cobrado: "Cobrado",
};

const ReceivablesPage: React.FC = () => {
  const [data, setData] = useState<Receivable | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/accounts-receivable").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
      </div>
    );
  if (!data) return null;

  return (
    <div data-testid="receivables-page">
      <PageHeader
        title="Cuentas por cobrar"
        subtitle="Ventas que aún no se convierten en dinero disponible"
        actions={
          <button
            onClick={async () => {
              try {
                await downloadCsv("pendientes");
                toast.success("Exportado: pendientes.csv");
              } catch {
                toast.error("Error al exportar");
              }
            }}
            data-testid="receivables-export"
            className="flex items-center gap-2 rounded-none border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Exportar
          </button>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="border border-slate-200 bg-white p-5" data-testid="cxc-total">
          <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Pendiente total</span>
          <div className="mt-3 text-2xl font-medium tabular text-slate-900">{money(data.total)}</div>
        </div>
        {data.by_channel.map((c) => (
          <div key={c.channel} className="border border-slate-200 bg-white p-5" data-testid={`cxc-${c.channel}`}>
            <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">Pendiente {c.channel}</span>
            <div className="mt-3 text-2xl font-medium tabular text-slate-900">{money(c.pendiente)}</div>
          </div>
        ))}
      </div>

      <div className="border border-slate-200 bg-white">
        {data.items.length === 0 ? (
          <EmptyState message="No hay cuentas por cobrar" testid="cxc-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="receivables-table">
            <thead>
              <tr className="bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Orden</th>
                <th className="px-5 py-3">Canal</th>
                <th className="px-5 py-3">Fecha venta</th>
                <th className="px-5 py-3 text-right">Vendido</th>
                <th className="px-5 py-3 text-right">Cobrado</th>
                <th className="px-5 py-3 text-right">Liquidado</th>
                <th className="px-5 py-3 text-right">Pendiente</th>
                <th className="px-5 py-3 text-right">Atraso</th>
                <th className="px-5 py-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`cxc-row-${o.id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-700">{o.order_number}</td>
                  <td className="px-5 py-3 text-slate-600">{o.channel}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-5 py-3 text-right tabular text-slate-700">{money(o.total)}</td>
                  <td className="px-5 py-3 text-right tabular text-emerald-600">{money(o.monto_cobrado)}</td>
                  <td className="px-5 py-3 text-right tabular text-teal-600">{money(o.monto_liquidado)}</td>
                  <td className="px-5 py-3 text-right font-medium tabular text-amber-700">{money(o.pendiente)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`inline-flex items-center gap-1 tabular ${(o.dias_atraso || 0) > 10 ? "text-red-600" : "text-slate-500"}`}>
                      <Clock className="h-3 w-3" /> {o.dias_atraso}d
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                      {SETTLEMENT_LABELS[o.settlement_status || ""] || o.settlement_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ReceivablesPage;
